import { Component, Input, OnInit, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Guid } from 'guid-typescript';
import { AdvertisementModel } from 'src/app/models/advertisement.model';
import { FileServerModel } from 'src/app/models/file-server.model';
import { UploaderSettingModel } from 'src/app/models/uploaderSetting.model';
import { AdvertisementService } from 'src/app/services/advertisement.service';
import { FileServerService } from 'src/app/services/file-server.service';
import { ToastService } from 'src/app/services/toast.service';
import { CommonFunctionsConfiguratorService } from 'src/app/shared/services/swal-fire-configurator.service';
import { MatIcon } from '@angular/material/icon';

import { MatFormField, MatInput, MatHint, MatError } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { FileUploaderComponent } from '../../shared/components/file-uploader/file-uploader.component';

import { MatButton } from '@angular/material/button';
import { PageHeaderComponent } from 'src/app/shared/ui/page-header/page-header.component';
import { httpUrlValidator } from '../service-offer/http-url.validator';
import { EmptyStateComponent } from 'src/app/shared/ui/empty-state/empty-state.component';

/** Same limit as the Description column of Advertisements in the Products API (character varying(1000)). */
export const ADVERTISEMENT_DESCRIPTION_MAX_LENGTH = 1000;

@Component({
    selector: 'app-advertisement',
    templateUrl: './advertisement.component.html',
    styleUrls: ['./advertisement.component.scss'],
    imports: [MatIcon, FormsModule, ReactiveFormsModule, MatFormField, MatInput, MatHint, MatError, MatSlideToggle, FileUploaderComponent, MatButton, PageHeaderComponent, EmptyStateComponent]
})
export class AdvertisementComponent implements OnInit {
  fileServerSrv = inject(FileServerService);
  private readonly advertisementSrv = inject(AdvertisementService);
  toastr = inject(ToastService);
  private readonly swalFire = inject(CommonFunctionsConfiguratorService);


  @Input() settings!: UploaderSettingModel;
  advertisementForm = new UntypedFormGroup({});
  advertisements: AdvertisementModel[] = [];
  currentAdvertisement!: AdvertisementModel;

  uploadersetting!: UploaderSettingModel;
  readonly descriptionMaxLength = ADVERTISEMENT_DESCRIPTION_MAX_LENGTH;

  ngOnInit(): void {
    this.advertisementForm = this.getForm();
    this.syncActiveToggle();
    this.advertisementForm.controls['advertisementImg'].valueChanges.subscribe(() => this.syncActiveToggle());
    this.advertisementForm.controls['isActive'].valueChanges.subscribe(() => this.syncActiveToggle());

    this.uploadersetting = {
      accept: ['image/png', 'image/jpg', 'image/jpeg'],
      inputWhith: '50%',
      imgWhith: '324px',
      imgHeight: '188px',
      controller: 'fileServerSrv.Products',
      formGroup: this.advertisementForm,
      validationsHeight: 188,
      validationsWhith: 324,
      validationMbMaxSize: 2,
    };
  }

  ngAfterViewInit(): void {
    this.getAdvertisements();
  }

  onSubmit() {
    const description = this.advertisementForm.controls['description'];
    if (description.hasError('maxlength')) {
      description.markAsTouched();
      this.toastr.error(
        'Error',
        `La descripción no puede superar los ${this.descriptionMaxLength} caracteres (tiene ${description.value.length}).`
      );
      return;
    }
    if (this.advertisementForm.invalid) {
      this.advertisementForm.markAllAsTouched();
      this.toastr.warning('Atención', 'Completá los campos requeridos.');
      return;
    }
    if (this.showActivateError()) {
      this.toastr.error('Error', 'Agregá una imagen para poder activar la publicidad.');
      return;
    }
    // getRawValue: isActive is disabled while there is no image and must still be sent (as false).
    const payload = this.advertisementForm.getRawValue();
    let model = payload as AdvertisementModel;
    if (model.id == Guid.createEmpty().toString()) {
      this.advertisementSrv.post(payload).subscribe({
        next: (result: any) => {
          this.toastr.success(
            'Nueva publicidad',
            'Se guardó la nueva publicidad.'
          );
          this.cleanForm();
          this.advertisementForm.controls['id'].setValue(Guid.EMPTY);
          this.getAdvertisements();
        },
        error: () => {
          this.toastr.error(
            'Error',
            'No se pudo guardar la nueva publicidad.'
          );
        },
      });
    } else {
      this.advertisementSrv.patch(payload).subscribe({
        next: (result: any) => {
          if (
            this.advertisementForm.controls.advertisementImg.value &&
            !this.advertisementForm.controls.advertisementImg.value.id
          ) {
            let fileServerModel = this.advertisementForm.controls
              .advertisementImg.value as FileServerModel;
            this.fileServerSrv
              .UploadTaskFile(
                model.id.toString(),
                fileServerModel,
                this.fileServerSrv.Products
              )
              .subscribe();
          }
          this.toastr.success(
            'Edición de publicidad',
            'Se ha actualizado la publicidad exitosamente'
          );
          this.getAdvertisements();
          this.cleanForm();
        },
        error: () => {
          this.toastr.error('Error', 'No se pudo guardar la publicidad.');
        },
      });
    }
  }

  getAdvertisements() {
    this.advertisementSrv.get().subscribe((data: AdvertisementModel[]) => {
      this.advertisements = data;
    });
  }

  editAdvertisement(advertisement: AdvertisementModel) {
    this.currentAdvertisement = advertisement;
    this.getAdvertisementDetails(advertisement.id);
  }

  getAdvertisementDetails(advertisementId: string) {
    this.cleanForm();
    this.advertisementSrv
      .getAdvertisement(advertisementId)
      .subscribe((details) => {
        this.advertisementForm.patchValue(details);

        if (details.file != null) {
          let model: FileServerModel = {
            id: details.file.fileId.toString(),
            groupId: advertisementId.toString(),
            name: 'advertisementImg',
            imageSrc: details.file.url,
          };
          this.advertisementForm.controls[model.name].patchValue(model);
        }
      });
  }

  deleteAdvertisement(advert: AdvertisementModel) {
    let advId = advert.id;
    this.swalFire
      .SwalFireQuestion(
        'Eliminar publicidad',
        '¿Está seguro que desea eliminar esta publicidad?',
        'Eliminar',
        'Cancelar'
      )
      .then((result) => {
        if (result.isConfirmed) {
          this.fileServerSrv
            .GetTaskFiles(advId, this.fileServerSrv.Products)
            .subscribe((dataimg) => {
              dataimg.forEach((img) => {
                this.fileServerSrv.delete(img.id).subscribe();
              });
            });

          this.advertisementSrv.delete(advert.id.toString()).subscribe(
            (data) => {
              this.toastr.success(
                'Actualización',
                'La publicidad se eliminó correctamente.'
              );
              // The ad no longer exists: clear the form instead of loading its (now empty) details.
              this.cleanForm();
              this.getAdvertisements();
            },
            (err) => {
              this.toastr.error('Error', 'No se pudo eliminar la publicidad.');
            }
          );
        }
      });
  }

  getForm(): UntypedFormGroup {
    return new UntypedFormGroup({
      id: new UntypedFormControl(Guid.EMPTY.toString()),
      name: new UntypedFormControl('', Validators.required),
      description: new UntypedFormControl('', [
        Validators.required,
        Validators.maxLength(ADVERTISEMENT_DESCRIPTION_MAX_LENGTH),
      ]),
      link: new UntypedFormControl('', [Validators.required, httpUrlValidator]),
      isActive: new UntypedFormControl(false, Validators.required),
      advertisementImg: new UntypedFormControl(null),
      file: new UntypedFormControl(null),
    });
  }

  deleteFileEvent(event: any) {
    if (event.delete && this.currentAdvertisement) {
      if (this.currentAdvertisement.isActive)
        this.advertisementSrv
          .getAdvertisement(this.currentAdvertisement.id)
          .subscribe((details) => {
            details.isActive = false;

            this.advertisementSrv.patch(details).subscribe((data) => {
              this.getAdvertisements();
              this.advertisementForm.controls.isActive.patchValue(false);
            });
          });
    }
  }

  cleanForm() {
    this.advertisementForm.patchValue(this.getForm().value);
    this.advertisementForm.markAsUntouched();
  }

  /** The list row of the advertisement open in the form (the form id is the selection). */
  isSelectedAdvertisement(advertisement: AdvertisementModel): boolean {
    return !this.isNew() && advertisement.id?.toString() === this.advertisementForm.controls.id.value?.toString();
  }

  isNew() {
    return (
      !this.advertisementForm.controls.id.value ||
      this.advertisementForm.controls.id.value == Guid.EMPTY
    );
  }

  /**
   * An advertisement needs an image to be active. [disabled] on a reactive control is ignored, so the toggle is
   * disabled through the form API: off while there is no image, but still enabled when an advertisement saved
   * active without image is opened, so it can be switched off. The uploader patches its control with
   * emitEvent: false, so the template also calls this on (sendImgsToParent).
   */
  syncActiveToggle(): void {
    const isActive = this.advertisementForm.controls['isActive'];
    const hasImage = !!this.advertisementForm.controls['advertisementImg'].value;
    if (hasImage || isActive.value) {
      isActive.enable({ emitEvent: false });
    } else {
      isActive.disable({ emitEvent: false });
    }
  }

  showActivateError() {
    return (
      !!this.advertisementForm.controls.isActive.value &&
      !this.advertisementForm.controls.advertisementImg.value
    );
  }
}
